import { api } from "../../api/client";

export type ApiTask = {
    id: number;
    title: string | null;
    body: string | null;
    urName?: string | null;
    companyName?: string | null;
    companyAddress?: string | null;
    company?: {
        name?: string | null;
        address?: string | null;
    } | null;
    author?: {
        name?: string | null;
    } | null;
    dateCreate: string | null;
    dateUpdate?: string | null;
    dateLastMess: string | null;
    terminalPaymentBlocked?: boolean;
    status?: {
        id: number;
        name: string | null;
        title?: string | null;
    } | null;
};

export type ApiResponse = { data: ApiTask[] };

export type TicketStatus = "open" | "closed" | "waiting";

export type Ticket = {
    id: number;
    title: string;
    displayTitle: string;
    objectLabel: string;
    createdAt: string;
    lastActivity: string;
    status: TicketStatus;
    statusLabel: string;
    terminalPaymentBlocked: boolean;
    isOldClosed: boolean;
};

const OLD_CLOSED_DAYS = 14;
const CLOSED_WORDS = ["закры", "отклон", "отмен", "реш", "архив", "done", "выполн"];
const WAITING_WORDS = ["ожид", "соглас"];

function fmt(dt: string | null | undefined): string {
    if (!dt) return "";
    const d = new Date(dt);
    if (isNaN(d.getTime())) return dt;
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
        d.getHours()
    )}:${pad(d.getMinutes())}`;
}

function statusText(status?: ApiTask["status"]): string {
    return String(status?.title || status?.name || "").trim();
}

function normalize(text: string): string {
    return text.trim().toLowerCase().replace(/ё/g, "е");
}

function mapStatus(status?: ApiTask["status"]): TicketStatus {
    const name = normalize(statusText(status));

    if (CLOSED_WORDS.some((word) => name.includes(word))) return "closed";
    if (WAITING_WORDS.some((word) => name.includes(word))) return "waiting";

    switch (status?.id) {
        case 9:
            return "waiting";
        default:
            return "open";
    }
}

function defaultStatusLabel(status: TicketStatus): string {
    switch (status) {
        case "closed":
            return "Закрыт";
        case "waiting":
            return "Ожидает ответа";
        default:
            return "Открыт";
    }
}

function parseDateMs(dt: string | null | undefined): number | null {
    if (!dt) return null;
    const time = new Date(dt).getTime();

    return Number.isFinite(time) ? time : null;
}

function firstNonEmpty(values: Array<string | null | undefined>): string {
    for (const value of values) {
        const text = String(value || "").trim();
        if (text) return text;
    }

    return "";
}

function objectLabel(task: ApiTask): string {
    return firstNonEmpty([
        task.companyAddress,
        task.company?.address,
        task.companyName,
        task.company?.name,
        task.urName,
        task.author?.name,
    ]);
}

export function ticketStatusClass(ticket: Ticket): string {
    return ticket.isOldClosed ? "archived" : ticket.status;
}

export function visibleTickets(tickets: Ticket[], showOldClosed: boolean): Ticket[] {
    if (showOldClosed) return tickets;

    return tickets.filter((ticket) => !ticket.isOldClosed);
}

export function oldClosedTicketCount(tickets: Ticket[]): number {
    return tickets.filter((ticket) => ticket.isOldClosed).length;
}

export async function loadTickets(path: string, companyId?: string | null, signal?: AbortSignal): Promise<Ticket[]> {
    const params = companyId ? { companyId } : undefined;
    const res = await api.get<ApiResponse>(path, params, { signal });
    const items: ApiTask[] = Array.isArray(res?.data) ? res!.data : [];
    const oldClosedThreshold = Date.now() - OLD_CLOSED_DAYS * 24 * 60 * 60 * 1000;

    return items.map((task) => {
        const status = mapStatus(task.status);
        const title = task.title || "Без названия";
        const closedAt = parseDateMs(task.dateUpdate || task.dateLastMess || task.dateCreate);
        const isOldClosed = status === "closed" && closedAt !== null && closedAt < oldClosedThreshold;
        const label = statusText(task.status) || defaultStatusLabel(status);

        return {
            id: task.id,
            title,
            displayTitle: `#${task.id} · ${title}`,
            objectLabel: objectLabel(task),
            createdAt: fmt(task.dateCreate),
            lastActivity: fmt(task.dateLastMess || task.dateUpdate || task.dateCreate),
            status,
            statusLabel: label,
            terminalPaymentBlocked: Boolean(task.terminalPaymentBlocked),
            isOldClosed,
        };
    });
}
